import type { TypographyVariantProps } from "../../atoms/typography";
import { iconSize } from "../../tokens";

type TypographyColor = NonNullable<TypographyVariantProps["color"]>;
type TypographyVariant = NonNullable<TypographyVariantProps["variant"]>;

/**
 * Label type per selection state: Figma's `text/body/sm`, Regular, and Medium
 * when selected (12/16 on phones, 14/20 from 768).
 */
export const sidebarNavItemLabelVariant: Record<"idle" | "selected", TypographyVariant> = {
  idle: "body-sm",
  selected: "body-sm-medium",
};

/**
 * Base expanded-row classes. Layout + transitions only — colors are layered
 * on top via `expandedStateClasses`. Figma's inset and gap are `padding-sm`:
 * 8 on phones, 12 from 768 (named by step until register row 9's desktop
 * scale lands). The vertical padding is `_cta/padding/lg`, 8 and 10, so a
 * desktop row is 40 as drawn.
 */
export const sidebarNavItemExpandedBaseClasses = [
  "gap-spacing-sm md:gap-spacing-md px-spacing-sm md:px-spacing-md py-cta-lg md:py-cta-lg-dt flex w-full flex-row items-center transition-colors",
  // Touch-target-token rows on touch screens and native, as Dropdown's, not
  // invisible hit areas: rows 4px apart would share them and each get 40 (DS-042)
  "native:min-h-touch-target [@media(pointer:coarse)]:min-h-touch-target",
].join(" ");

/**
 * Expanded-row radius by lines variant: Figma's one-line row is a pill, and
 * the two-line row binds `rounded-lg`, 12 and 16 from 768 (Ardhi, DQ18).
 */
export const sidebarNavItemExpandedRadiusClasses = {
  oneLine: "rounded-radius-full",
  twoLine: "rounded-radius-lg md:rounded-radius-xl",
} as const;

/**
 * Color classes for a row or chip by selection state. Hover behavior lives in
 * the same class string so the molecule stays prop-driven. Figma fills an idle
 * row with `neutral/background-default`, and draws the selected one in CTA2
 * (Ardhi, DQ17 (d)): its surface, and a 1px `CTA2-border-hover` stroke inside
 * that goes on hover (with a pointer that hovers). On web the stroke is an
 * inset ring, so the row keeps its
 * height; iOS and Android draw no ring, so they get
 * `sidebarNavItemSelectedBorderClasses`.
 */
export const sidebarNavItemExpandedStateClasses = {
  idle: "bg-luce-neutral-background-default hover:bg-luce-neutral-background-intermediate",
  selected: [
    "bg-luce-button-cta2-surface hover:bg-luce-button-cta2-surfaceHover",
    "web:ring-1 web:ring-inset web:ring-luce-button-cta2-borderHover",
    // Only a pointer that hovers drops it: a touch browser keeps :hover after a
    // tap, which would leave the selected row without its stroke
    "web:[@media(hover:hover)]:hover:ring-0",
  ].join(" "),
} as const;

/**
 * The selected stroke on iOS and Android: a border on a layer over the row,
 * with the row's corner, so the row keeps Figma's height (a border on the row
 * itself would add 2 to a two-line row). Web draws the inset ring instead.
 */
export const sidebarNavItemSelectedBorderClasses =
  "web:hidden pointer-events-none absolute inset-0 border border-luce-button-cta2-borderHover";

/**
 * Leading icon px: Figma's `icon-sm` 16 (one line) and `icon-md` 20 (two
 * lines) on phones, 20 / 24 from 768 on web. iOS and Android keep the phone
 * sizes: on a tablet the row's spacing and type step at 768 and the icon
 * doesn't, as IconButton's circle steps and its icon doesn't (the atoms
 * audit's tablet step, A3).
 */
export const sidebarNavItemIconSize = {
  oneLine: { mobile: iconSize.sm, desktop: iconSize.md },
  twoLine: { mobile: iconSize.md, desktop: iconSize.lg },
} as const;

/**
 * Collapsed-chip base: 40px square (the touch-target token on touch screens and
 * native), centered icon, tight padding. Radius is applied via
 * `sidebarNavItemCollapsedRadiusClasses`.
 */
export const sidebarNavItemCollapsedBaseClasses = [
  "p-spacing-xs flex size-10 items-center justify-center transition-colors",
  "native:size-touch-target [@media(pointer:coarse)]:size-touch-target",
].join(" ");

/** Collapsed-chip radius by lines variant: a pill for both (Ardhi, DQ17 (c)). */
export const sidebarNavItemCollapsedRadiusClasses = {
  oneLine: "rounded-radius-full",
  twoLine: "rounded-radius-full",
} as const;

/** Typography color tokens for the label per selection state. */
export const sidebarNavItemLabelColor: Record<"idle" | "selected", TypographyColor> = {
  idle: "neutral-foreground-default",
  selected: "button-cta2-hover",
};

/** Typography color token for the supporting (2-line) text. */
export const sidebarNavItemSupportingTextColor: TypographyColor = "neutral-foreground-intermediate";

/** Icon color class per selection state. Passed to `Icon`'s `className`. */
export const sidebarNavItemIconColorClass: Record<"idle" | "selected", string> = {
  idle: "text-luce-neutral-foreground-default",
  selected: "text-luce-button-cta2-hover",
};
